import { Button } from '@/components/ui/button';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';

import { DescriptionInput } from '@/engagements/base/admin/DescriptionInput';
import {
  Section,
  SectionContent,
  SectionHeading,
  SettingRowDescription,
  SettingRowHeading,
  SettingRowInput,
  SettingsRow,
  SettingsTable,
} from '@/components/Section';
import { NumericConfigInput } from '@/engagements/base/admin/NumericConfigInput';
import { QRCodeCtaInput } from '@/engagements/base/admin/QRCodeCtaInput';
import { TitleInput } from '@/engagements/base/admin/TitleInput';
import {
  VoteForAdminConfig as AdminConfig,
  useDeleteSubmissionMutation,
  useGetSubmissionsQuery,
  VoteForAdminData,
  VoteForSubmissionData,
} from '@/generated/graphql';
import { useAdminCreateSubmission } from '@/hooks/useAdminCreateSubmission';
import { useAdminEngagementContext } from '@/providers/AdminEngagementProvider/AdminEngagementContext';
import { AdminSubmissionProvider } from '@/providers/AdminSubmissionProvider/AdminSubmissionProvider';
import { PlusIcon } from '@radix-ui/react-icons';
import { EditableChoiceRow } from './EditableChoiceRow';
import {
  createSubmissionTypeFilter,
  SubmissionWithTypename,
} from '@/utils/createSubmissionTypeFilter';
import { useCallback, useMemo } from 'react';
import { dataChanged } from '@/providers/AdminEngagementProvider/actions';
import { keyBy } from 'lodash';
import { createLogger } from '@/utils/createLogger';

const logger = createLogger('VoteForEditorView');

const voteForSubmissionTypeFilter =
  createSubmissionTypeFilter<VoteForSubmissionData>('VoteForSubmissionData');

export function VoteForEditorView() {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    state: { editorMode, ...engagement },
    dispatch,
  } = useAdminEngagementContext();

  const choiceDisplayOrder = (engagement.data as VoteForAdminData)
    .choiceDisplayOrder;

  const { data: submissionsData, refetch: refetchSubmissions } =
    useGetSubmissionsQuery({
      variables: {
        engagementId: engagement.id,
      },
      fetchPolicy: 'network-only',
    });

  const [deleteSubmission] = useDeleteSubmissionMutation();

  const choices = useMemo(() => {
    logger.info('recomputing choices');
    if (!submissionsData) return [];
    const submissionsById = keyBy(submissionsData?.submissions, 'id');
    return (choiceDisplayOrder || [])
      .map((id) => submissionsById[id])
      .filter(Boolean)
      .filter(voteForSubmissionTypeFilter);
  }, [choiceDisplayOrder, submissionsData]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      // Get current indexes
      const oldIndex = choiceDisplayOrder.findIndex((s) => s === active.id);
      const newIndex = choiceDisplayOrder.findIndex((s) => s === over.id);

      // Reorder array
      const updatedOrder = [...choiceDisplayOrder];
      const [movedItem] = updatedOrder.splice(oldIndex, 1);
      updatedOrder.splice(newIndex, 0, movedItem);

      dispatch(dataChanged({ data: { choiceDisplayOrder: updatedOrder } }));
    },
    [choiceDisplayOrder, dispatch],
  );

  return (
    <>
      <Section>
        <SectionHeading>Settings</SectionHeading>
        <SectionContent>
          <SettingsTable>
            <SettingsRow>
              <SettingRowHeading>Title</SettingRowHeading>
              <SettingRowDescription>
                Shows as a pulsing title above the QR code. Keep it short.
              </SettingRowDescription>
              <SettingRowInput>
                <TitleInput />
              </SettingRowInput>
            </SettingsRow>

            <SettingsRow>
              <SettingRowHeading>Instructions</SettingRowHeading>
              <SettingRowDescription>
                Shown as instructions to fans on mobile.
              </SettingRowDescription>
              <SettingRowInput>
                <DescriptionInput />
              </SettingRowInput>
            </SettingsRow>

            <SettingsRow>
              <SettingRowHeading>QR code label</SettingRowHeading>
              <SettingRowDescription>
                Displayed above the QR code. Keep it short.
              </SettingRowDescription>
              <SettingRowInput>
                <QRCodeCtaInput />
              </SettingRowInput>
            </SettingsRow>
            <SettingsRow>
              <SettingRowHeading>Votes per user</SettingRowHeading>
              <SettingRowDescription>
                The number of votes a user can cast.
              </SettingRowDescription>
              <SettingRowInput>
                <NumericConfigInput<AdminConfig>
                  id="votes-per-user"
                  name="votes-per-user"
                  configKey="votesPerUser"
                  min={1}
                  max={10}
                />
              </SettingRowInput>
            </SettingsRow>
          </SettingsTable>
        </SectionContent>
        {/* <pre>{JSON.stringify(engagement.config, null, 2)}</pre> */}
      </Section>
      <Section>
        <SectionHeading>Choices</SectionHeading>
        <SectionContent>
          <SettingsTable>
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={choiceDisplayOrder ?? []}
                strategy={verticalListSortingStrategy}
              >
                {choices.map((submission) => (
                  <AdminSubmissionProvider<VoteForSubmissionData>
                    key={submission.id}
                    submission={submission}
                  >
                    <EditableChoiceRow
                      onDelete={(id) => {
                        deleteSubmission({
                          variables: {
                            submissionId: id,
                          },
                        });
                        dispatch(
                          dataChanged({
                            data: {
                              choiceDisplayOrder: choiceDisplayOrder.filter(
                                (s) => s !== id,
                              ),
                            },
                          }),
                        );
                      }}
                    />
                  </AdminSubmissionProvider>
                ))}
              </SortableContext>
            </DndContext>
          </SettingsTable>
          <div className="flex justify-center">
            <AddChoiceButton
              className="m-4"
              onSuccess={(submission) => {
                logger.info('AddChoiceButton onSuccess', {
                  submission,
                });
                dispatch(
                  dataChanged({
                    data: {
                      choiceDisplayOrder: [
                        ...choiceDisplayOrder,
                        submission.id,
                      ],
                    },
                  }),
                );
                refetchSubmissions();
              }}
            />
          </div>
        </SectionContent>
      </Section>
    </>
  );
}

function AddChoiceButton({
  className,
  onSuccess,
}: {
  className?: string;
  onSuccess?: (
    submission: SubmissionWithTypename<VoteForSubmissionData>,
  ) => void;
}) {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    state: { editorMode, ...engagement },
  } = useAdminEngagementContext();

  const { createSubmission } =
    useAdminCreateSubmission<VoteForSubmissionData>();

  const handleAddChoice = () => {
    createSubmission({
      engagementId: engagement.id,
      data: {},
      onSuccess,
    });
  };
  return (
    <Button onClick={handleAddChoice} className={className}>
      <PlusIcon />
      Add Choice
    </Button>
  );
}
